import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBlogPost } from "@/lib/api";
import { ArrowLeft, Calendar } from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import Seo from "@/components/Seo";
import { SITE_URL, SITE_NAME } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    getBlogPost(slug)
      .then(setPost)
      .catch((err) => setError(err?.response?.status === 404 ? "Post not found" : "Failed to load post"))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-post-page" className="text-white">
      {post && (
        <Seo
          title={`${post.title} · ${SITE_NAME} Blog`}
          description={post.excerpt || post.content.slice(0, 160)}
          image={post.cover_url || undefined}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_URL}/blog/${post.slug}`,
            },
            headline: post.title,
            description: post.excerpt,
            image: post.cover_url || `${SITE_URL}/media/og-share-card.png`,
            datePublished: post.created_at,
            dateModified: post.updated_at,
            articleSection: post.category,
            author: { "@type": "Organization", name: SITE_NAME },
            publisher: {
              "@type": "Organization",
              name: SITE_NAME,
              logo: { "@type": "ImageObject", url: `${SITE_URL}/media/anytime-vending-machines-logo.png` },
            },
          }}
        />
      )}
      <article className="mx-auto max-w-4xl px-4 md:px-8 lg:px-12 py-16">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-[#FE7200]">
          <ArrowLeft className="h-4 w-4" /> Back to all posts
        </Link>

        {loading && <div className="mt-8 text-zinc-400">Loading…</div>}
        {error && (
          <div data-testid="post-error" className="mt-8 rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-red-200">
            {error}
          </div>
        )}
        {post && (
          <>
            <div className="mt-6 flex items-center gap-3 text-xs">
              <span className="inline-flex items-center rounded-full bg-[#FE7200] px-3 py-1 font-bold uppercase tracking-[0.18em] text-black">
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1 text-zinc-400">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(post.created_at).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
              {post.title}
            </h1>
            {post.cover_url && (
              <img src={post.cover_url} alt={post.title} className="mt-8 w-full rounded-2xl border border-white/10 object-cover max-h-[420px]" />
            )}
            <div className="mt-8 whitespace-pre-line text-base md:text-lg leading-relaxed text-zinc-200">
              {post.content}
            </div>

            <div className="mt-12 rounded-2xl border border-[#FE7200]/30 bg-gradient-to-r from-[#FE7200]/15 to-transparent p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="font-display text-xl md:text-2xl font-bold text-white">Think you qualify?</div>
                <p className="text-sm text-zinc-300 mt-1">Book a free site visit and we'll confirm eligibility.</p>
              </div>
              <a
                href="#book-form"
                data-testid="post-cta-button"
                className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
              >
                Book Free Site Visit
              </a>
            </div>
          </>
        )}
      </article>

      {post && (
        <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 pb-16" data-testid="post-form-section">
          <SiteVisitForm source={`blog-post-${post.slug}`} testIdPrefix="post-booking" />
        </section>
      )}
    </div>
  );
}
